// One call's LiveKit room, like the app's LiveKitRoomSession (app/.../call/LiveKitRoomSession.kt):
// identity = user id (the token carries nothing else), adaptive stream and dynacast, the
// microphone on at connect; call messages as plain strings on the reliable data topic "voxie.call";
// the voice agent's transcript as text streams on "lk.transcription". No E2EE (anrufe-konzept §5).

import { ConnectionQuality, type Participant, type RemoteTrack, Room, RoomEvent, Track, type TrackPublication } from "livekit-client";

export const CALL_TOPIC = "voxie.call";

export interface Peer {
  identity: string;
  isAgent: boolean;
  speaking: boolean;
  micMuted: boolean;
  /** Camera or (preferred) screen share; null when there is no picture. */
  video: TrackPublication | null;
  screen: boolean;
  weak: boolean;
  /** Voice agent: listening | thinking | speaking (attribute lk.agent.state). */
  agentState?: string;
}

export interface AgentLine {
  id: string;
  mine: boolean;
  text: string;
  final: boolean;
}

export interface RoomEvents {
  onChange: () => void;
  onMessage: (from: string, text: string) => void;
  onAgentLine: (line: AgentLine) => void;
  onReconnecting: (reconnecting: boolean) => void;
  onDisconnected: () => void;
  onPeerJoined: (identity: string) => void;
  onPeerLeft: (identity: string) => void;
}

export class CallRoom {
  readonly room = new Room({ adaptiveStream: true, dynacast: true, videoCaptureDefaults: { resolution: { width: 1280, height: 720, frameRate: 30 } } });
  private readonly audio = new Map<string, HTMLMediaElement>();
  private readonly events: RoomEvents;

  constructor(events: RoomEvents) {
    this.events = events;
    const r = this.room;
    const changed = () => events.onChange();
    r.on(RoomEvent.ParticipantConnected, (p) => (events.onPeerJoined(p.identity), changed()))
      .on(RoomEvent.ParticipantDisconnected, (p) => (events.onPeerLeft(p.identity), changed()))
      .on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
        if (track.kind === Track.Kind.Audio) this.playAudio(track);
        changed();
      })
      .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        const key = track.sid ?? track.mediaStreamTrack.id;
        this.audio.get(key)?.remove();
        this.audio.delete(key);
        track.detach();
        changed();
      })
      .on(RoomEvent.TrackMuted, changed)
      .on(RoomEvent.TrackUnmuted, changed)
      .on(RoomEvent.LocalTrackPublished, changed)
      .on(RoomEvent.LocalTrackUnpublished, changed)
      .on(RoomEvent.ActiveSpeakersChanged, changed)
      .on(RoomEvent.ConnectionQualityChanged, changed)
      .on(RoomEvent.ParticipantAttributesChanged, changed)
      .on(RoomEvent.Reconnecting, () => events.onReconnecting(true))
      .on(RoomEvent.Reconnected, () => events.onReconnecting(false))
      .on(RoomEvent.Disconnected, () => events.onDisconnected())
      .on(RoomEvent.DataReceived, (payload, participant, _kind, topic) => {
        if (topic === CALL_TOPIC && participant) events.onMessage(participant.identity, new TextDecoder().decode(payload));
      });
    r.registerTextStreamHandler("lk.transcription", async (reader, info) => {
      const id = reader.info.attributes?.["lk.segment_id"] ?? reader.info.id;
      const final = reader.info.attributes?.["lk.transcription_final"] === "true";
      const mine = info.identity === r.localParticipant.identity;
      let text = "";
      for await (const chunk of reader) {
        text += chunk;
        events.onAgentLine({ id, mine, text, final: false });
      }
      events.onAgentLine({ id, mine, text, final });
    });
  }

  async connect(url: string, token: string, camera: boolean): Promise<void> {
    await this.room.connect(url, token);
    await this.room.localParticipant.setMicrophoneEnabled(true);
    if (camera) await this.room.localParticipant.setCameraEnabled(true);
    this.events.onChange();
  }

  get remoteCount(): number {
    return this.room.remoteParticipants.size;
  }

  peers(): Peer[] {
    return [...this.room.remoteParticipants.values()].map((p) => this.peerOf(p));
  }

  me(): Peer {
    return this.peerOf(this.room.localParticipant);
  }

  async setMic(on: boolean) {
    await this.room.localParticipant.setMicrophoneEnabled(on);
    this.events.onChange();
  }

  async setCamera(on: boolean) {
    await this.room.localParticipant.setCameraEnabled(on);
    this.events.onChange();
  }

  /** Throws when the user cancels the browser's picker. */
  async setScreen(on: boolean) {
    await this.room.localParticipant.setScreenShareEnabled(on, { audio: false });
    this.events.onChange();
  }

  get micOn(): boolean {
    return this.room.localParticipant.isMicrophoneEnabled;
  }

  get cameraOn(): boolean {
    return this.room.localParticipant.isCameraEnabled;
  }

  get screenOn(): boolean {
    return this.room.localParticipant.isScreenShareEnabled;
  }

  /** The microphone as a track, for the captions' transcription. */
  micTrack(): MediaStreamTrack | undefined {
    return this.room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track?.mediaStreamTrack;
  }

  send(text: string, to?: string[]) {
    void this.room.localParticipant
      .publishData(new TextEncoder().encode(text), { reliable: true, topic: CALL_TOPIC, destinationIdentities: to })
      .catch(() => undefined);
  }

  async disconnect() {
    for (const el of this.audio.values()) el.remove();
    this.audio.clear();
    await this.room.disconnect();
  }

  private playAudio(track: RemoteTrack) {
    const el = track.attach();
    el.style.display = "none";
    document.body.appendChild(el);
    this.audio.set(track.sid ?? track.mediaStreamTrack.id, el);
  }

  private peerOf(p: Participant): Peer {
    const screen = p.getTrackPublication(Track.Source.ScreenShare);
    const camera = p.getTrackPublication(Track.Source.Camera);
    const video = screen && !screen.isMuted && screen.track ? screen : camera && !camera.isMuted && camera.track ? camera : null;
    const mic = p.getTrackPublication(Track.Source.Microphone);
    return {
      identity: p.identity,
      isAgent: p.isAgent || p.identity.startsWith("agent-"),
      speaking: p.isSpeaking,
      micMuted: !mic || mic.isMuted,
      video,
      screen: video === screen,
      weak: p.connectionQuality === ConnectionQuality.Poor || p.connectionQuality === ConnectionQuality.Lost,
      agentState: p.attributes?.["lk.agent.state"],
    };
  }
}
